import React, { useState, useEffect } from "react";
import {
    ReloadOutlined,
    DollarOutlined,
    UserOutlined,
    InfoCircleOutlined,
    MailOutlined,
    PhoneOutlined,
    CalendarOutlined,
    ShopOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    TagOutlined,
    CheckCircleOutlined,
    LockOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import { Tag, Modal, Button, Col, Space, Select, Typography, Switch, message, Row, Card, Input, Tooltip } from "antd";
import Breadcrumbs from "../../component/Breadcrumbs";
import PayoutServ from "../../service/PayoutServ";
import ShopServ from "../../service/ShopServ";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import { usePreference } from "../../context/PreferenceContext";
import dayjs from "dayjs";
import { formatPaymentDisplay } from "../../util/paymentFormatter";
import { useAdminTable } from "../hooks/useAdminTable";

const { Text, Title } = Typography;
const { Option } = Select;

// Premium Warning Pulse Payout Status Switching Badge
const PayoutStatusBadge = ({ isPaid, loading, onChange }) => {
    const bg = isPaid ? "#f0fdf4" : "#fff5f5";
    const border = isPaid ? "1px solid #bbf7d0" : "1.5px solid #feb2b2";
    const color = isPaid ? "#16a34a" : "#dc2626";
    const pulseClass = !isPaid ? "payout-pulse-warning" : "";

    return (
        <div
            className={pulseClass}
            style={{
                background: bg,
                border: border,
                borderRadius: "12px",
                padding: "2px 8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                boxShadow: isPaid ? "0 0 8px rgba(34, 197, 94, 0.15)" : "none",
                transition: "all 0.3s ease",
                width: "135px"
            }}
            onClick={e => e.stopPropagation()}
        >
            <Select
                value={isPaid ? "PAID_OUT" : "NOT_PAID"}
                loading={loading}
                onChange={(val) => onChange(val === "PAID_OUT")}
                bordered={false}
                style={{ width: '100%' }}
                dropdownStyle={{ borderRadius: '8px', minWidth: '135px' }}
                suffixIcon={<span style={{ color: color, fontSize: '10px' }}>▼</span>}
            >
                <Option value="NOT_PAID">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '2px' }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: '#dc2626', display: "inline-block" }} />
                        <span style={{ fontSize: "11px", fontWeight: 800, color: '#dc2626', textTransform: "uppercase" }}>Pending</span>
                    </div>
                </Option>
                <Option value="PAID_OUT">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '2px' }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: '#16a34a', display: "inline-block" }} />
                        <span style={{ fontSize: "11px", fontWeight: 800, color: '#16a34a', textTransform: "uppercase" }}>Paid</span>
                        {isPaid && <LockOutlined style={{ color: '#fbbf24', fontSize: '12px' }} />}
                    </div>
                </Option>
            </Select>
        </div>
    );
};

const SellerPayoutHistory = () => {
    const { formatPrice } = usePreference();

    const [shops, setShops] = useState([]);
    
    // UI state
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toggleLoading, setToggleLoading] = useState({});

    // Reversal Modal State
    const [revertModalVisible, setRevertModalVisible] = useState(false);
    const [revertRecord, setRevertRecord] = useState(null);
    const [revertReason, setRevertReason] = useState("");
    const [revertingLoading, setRevertingLoading] = useState(false);

    // Paid Confirmation Modal State
    const [confirmPaidModalVisible, setConfirmPaidModalVisible] = useState(false);
    const [confirmPaidRecord, setConfirmPaidRecord] = useState(null);
    const [confirmPaidLoading, setConfirmPaidLoading] = useState(false);
    
    // Master stats fetched from API
    const [stats, setStats] = useState({
        totalRecords: 0,
        totalNotPaidAmount: 0,
        pendingPayoutCount: 0,
        pendingSellerCount: 0,
        totalPaidOutAmount: 0,
        totalPaidOutCount: 0,
        thisMonthPayout: 0,
        todayPayout: 0,
    });

    const [orders, setOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [loading, setLoading] = useState(false);
    
    // Pagination & Filters State
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [shopFilter, setShopFilter] = useState("");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [quickFilter, setQuickFilter] = useState(undefined);

    const [localLoading, setLocalLoading] = useState(false);

    const fetchPayouts = React.useCallback(async (p = page, size = pageSize, search = searchText, status = statusFilter, shop = shopFilter, payment = paymentMethodFilter, drange = dateRange) => {
        try {
            setLoading(true);
            const queryParams = {
                limit: size,
                page: p
            };
            if (search) queryParams.search = search;
            if (status) queryParams.payoutStatus = status;
            if (shop) queryParams.shopId = shop;
            if (payment) queryParams.paymentMethod = payment;
            if (drange?.[0]) queryParams.startDate = drange[0].startOf("day").toISOString();
            if (drange?.[1]) queryParams.endDate = drange[1].endOf("day").toISOString();
            
            const res = await PayoutServ.getSellerPayouts(queryParams);
            if (res?.stats) {
                setStats(res.stats);
            }
            setOrders(res?.data || []);
            setTotalOrders(res?.total || 0);
        } catch (error) {
            console.error("Error fetching seller payouts:", error);
            message.error("Failed to load seller payouts");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, searchText, statusFilter, shopFilter, paymentMethodFilter, dateRange]);

    useEffect(() => {
        fetchPayouts(page, pageSize, searchText, statusFilter, shopFilter, paymentMethodFilter, dateRange);
    }, [fetchPayouts, page, pageSize, searchText, statusFilter, shopFilter, paymentMethodFilter, dateRange]);

    const handleSearch = (v) => { setSearchText(v); setPage(1); };
    const refresh = () => fetchPayouts();

    const fetchShops = async () => {
        try {
            const res = await ShopServ.adminIndex();
            const shopList = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
            setShops(shopList);
        } catch (error) {
            console.error("Error fetching shops list:", error);
        }
    };

    useEffect(() => {
        fetchShops();
    }, []);

    // ─── Optimistic payout toggle ─────────────────────────────────────────────
    const handleTogglePayout = async (record, checked) => {
        const orderId = record._id;

        // Payout Reversal Protection
        if (!checked && record.sellerPayoutStatus === "PAID_OUT") {
            setRevertRecord(record);
            setRevertReason("");
            setRevertModalVisible(true);
            return;
        }

        if (checked && record.sellerPayoutStatus !== "PAID_OUT") {
            setConfirmPaidRecord(record);
            setConfirmPaidModalVisible(true);
            return;
        }

        const newStatus = checked ? "PAID_OUT" : "NOT_PAID";
        await processPayoutUpdate(orderId, newStatus, record);
    };

    const processPayoutUpdate = async (orderId, newStatus, record, reason = null) => {
        try {
            setToggleLoading(prev => ({ ...prev, [orderId]: true }));
            const updated = await PayoutServ.updateSellerPayoutStatus(orderId, newStatus, reason);
            const updatedRecord = updated && updated._id ? updated : { ...record, sellerPayoutStatus: newStatus, sellerPayoutDate: newStatus === "PAID_OUT" ? new Date().toISOString() : null };
            setOrders(prev => prev.map(o => o._id === orderId ? updatedRecord : o));
            message.success(`Order #${record.orderId} marked as ${newStatus === "PAID_OUT" ? "Paid Out" : "Pending"}`);
            refresh();
            return true;
        } catch (error) {
            console.error("Failed to toggle seller payout status:", error);
            message.error(error?.response?.data?.message || "Failed to update payout status");
            return false;
        } finally {
            setToggleLoading(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const confirmReversal = async () => {
        if (!revertReason.trim()) {
            message.error("Please provide a reason for reversing the payout.");
            return;
        }

        setRevertingLoading(true);
        const success = await processPayoutUpdate(revertRecord._id, "NOT_PAID", revertRecord, revertReason);
        setRevertingLoading(false);

        if (success) {
            setRevertModalVisible(false);
            setRevertRecord(null);
        }
    };

    const confirmPaidStatus = async () => {
        if (!confirmPaidRecord) return;
        setConfirmPaidLoading(true);
        const success = await processPayoutUpdate(confirmPaidRecord._id, "PAID_OUT", confirmPaidRecord);
        setConfirmPaidLoading(false);
        if (success) {
            setConfirmPaidModalVisible(false);
            setConfirmPaidRecord(null);
        }
    };

    const handleRowClick = async (record) => {
        try {
            setLocalLoading(true);
            const res = await PayoutServ.getSellerPayoutDetails(record._id);
            setSelectedRecord(res);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Failed to get payout details:", error);
            message.error("Failed to load payout details");
        } finally {
            setLocalLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRecord(null);
    };

    // Calculate seller earnings helper
    const getOrderTotals = (items = []) => {
        return items.reduce((acc, item) => {
            const qty = item.quantity || 1;
            acc.mrp += (item.mrp || item.price || 0) * qty;
            acc.sellerPrice += (item.sellerPrice || item.price || 0) * qty;
            return acc;
        }, { mrp: 0, sellerPrice: 0 });
    };

    const columns = [
        {
            title: "Order Code",
            key: "orderId",
            render: (_, record) => (
                <div>
                    <span style={{ fontWeight: 800, color: '#4776E6', fontSize: '13px', textDecoration: 'underline' }}>
                        #{record.orderId}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {record.items?.length || 0} item{record.items?.length !== 1 ? 's' : ''}
                    </div>
                </div>
            )
        },
        {
            title: "Vendor Code",
            key: "shopId",
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>
                        {record.shopId?.shopId || record.shop?.shopId || "—"}
                    </span>
                </div>
            )
        },
        {
            title: "Vendor Name",
            key: "shop",
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShopOutlined style={{ color: '#64748b' }} />
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                        {record.shopId?.name || record.shop?.name || "—"}
                    </span>
                </div>
            )
        },
        {
            title: "Vendor Final Earnings",
            key: "sellerEarnings",
            align: "right",
            render: (_, record) => {
                const totals = getOrderTotals(record.items);
                const sellerEarn = totals.sellerPrice - (record.couponDiscount || 0);
                return (
                    <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>
                        {formatPrice(sellerEarn)}
                    </span>
                );
            }
        },
        {
            title: "Delivery Charge",
            key: "deliveryCharge",
            align: "right",
            render: (_, record) => (
                <span style={{ color: '#1e293b', fontWeight: 600, fontSize: '13px' }}>
                    {formatPrice((record.deliveryCharge || 0) + (record.extraDeliveryCharge || 0))}
                </span>
            )
        },
        {
            title: "Admin Commission",
            key: "commission",
            align: "right",
            render: (_, record) => {
                const totals = getOrderTotals(record.items);
                const sellerEarn = totals.sellerPrice - (record.couponDiscount || 0);
                const commission = (record.totalAmount || 0) - sellerEarn - (record.deliveryCharge || 0) - (record.extraDeliveryCharge || 0);
                return (
                    <span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '13px' }}>
                        {formatPrice(commission)}
                    </span>
                );
            }
        },
        {
            title: "Delivered Amount",
            key: "deliveredAmount",
            align: "right",
            render: (_, record) => (
                <span style={{ color: '#1e293b', fontWeight: 600, fontSize: '13px' }}>
                    {formatPrice(record.totalAmount || 0)}
                </span>
            )
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            align: "center",
            render: (_, record) => {
                const method = formatPaymentDisplay(
                    record.paymentMethod || record.paymentType || 'COD',
                    record.paymentCollectionMethod || record.paymentMethod || record.paymentType,
                    record.paymentSubMethod
                );
                return (
                    <Tag color={method.startsWith('COD') && !method.includes('ONLINE') ? 'default' : 'cyan'} style={{ borderRadius: 6, fontSize: 11, fontWeight: 600, margin: 0 }}>
                        {method.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Delivered Date",
            key: "deliveredDate",
            render: (_, record) => {
                const deliveredDate = record.deliveredAt ||
                    record.statusHistory?.find(h => h.status === 'delivered')?.timestamp ||
                    record.updatedAt;
                return (
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {deliveredDate ? dayjs(deliveredDate).format('DD MMM YYYY') : "—"}
                    </span>
                );
            }
        },
        {
            title: "Payout Status",
            key: "payoutStatus",
            width: 145,
            align: 'center',
            render: (_, record) => {
                const isPaid = record.sellerPayoutStatus === "PAID_OUT";
                return (
                    <PayoutStatusBadge
                        isPaid={isPaid}
                        loading={toggleLoading[record._id]}
                        onChange={(checked) => handleTogglePayout(record, checked)}
                    />
                );
            }
        },
        {
            title: "Payout Date",
            key: "payoutDate",
            render: (_, record) => (
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                    {record.sellerPayoutDate ? dayjs(record.sellerPayoutDate).format('DD MMM YYYY, hh:mm A') : "—"}
                </span>
            )
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            render: (_, record) => {
                const { formatPaymentDisplay } = require('../../util/paymentFormatter');
                const method = formatPaymentDisplay(
                    record.paymentMethod || record.paymentType || record.deliveryPaymentMethod || "COD",
                    record.paymentCollectionMethod || record.paymentMethod || record.paymentType,
                    record.paymentSubMethod
                );
                return (
                    <Tag color={method.startsWith('COD') && !method.includes('ONLINE') ? 'default' : 'cyan'} style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }}>
                        {method.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Transaction ID",
            key: "transactionId",
            render: (_, record) => (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                    {record.transactionId || "-"}
                </span>
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            width: 70,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            ),
        }
    ];

    const fetchAllPayouts = async () => {
        return orders;
    };

    return (
        <div className="pcu-padding-24">
            {/* Inject warning pulse stylesheet dynamically */}
            <style>{`
                @keyframes pulse-warning-glow {
                    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
                }
                .payout-pulse-warning {
                    animation: pulse-warning-glow 2s infinite;
                }
                .payout-stat-card {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    cursor: pointer;
                    user-select: none;
                }
                .payout-stat-card:hover {
                    transform: translateY(-3px) scale(1.02) !important;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
                }
                .payout-stat-card:active {
                    transform: translateY(-1px) scale(1) !important;
                }
                .payout-stat-card.active-card:hover {
                    transform: scale(1.025) !important;
                }
            `}</style>

            <div className="pcu-margin-bottom-16">
                <Breadcrumbs items={[{ title: "Admin" }, { title: "Seller Payout History" }]} />
            </div>

            <div className="sd-container" style={{ padding: '24px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                {/* Header Section */}
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                            <DollarOutlined style={{ fontSize: '22px', color: '#4776E6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Seller Payout History</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{orders.length} completed transactions in seller payout ledger</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <TableActions
                            onFetchFullData={fetchAllPayouts}
                            columns={columns}
                            fileName="seller_payout_history"
                            pageName="Seller Payout History"
                        />
                        <ResetButton
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearchText("");
                                setStatusFilter("");
                                setShopFilter("");
                                setPaymentMethodFilter("");
                                setYearFilter("");
                                setMonthFilter("");
                                setDateRange(null);
                                setQuickFilter(undefined);
                                fetchPayouts();
                            }}
                            text="Refresh"
                        />
                    </div>
                </div>

                {/* Filters Section */}
                <div className="pcu-margin-bottom-24">
                    {/* Row 1: Search + QuickFilter + Filter By Shop */}
                    <FilterBar>
                        <Col xs={24} md={8}>
                            <SearchInput
                                label="Search"
                                placeholder="Search by Order ID, Shop or Seller..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <QuickFilter
                                value={quickFilter}
                                onChange={(key, range) => {
                                    setQuickFilter(key);
                                    setDateRange(range);
                                }}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Filter By Shop
                                </div>
                                <Select
                                    value={shopFilter}
                                    onChange={setShopFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="All Shops"
                                >
                                    <Option value="">All Shops</Option>
                                    {shops.map(shop => (
                                        <Option key={shop._id} value={shop._id}>
                                            {shop.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </FilterBar>

                    {/* Row 2: Status + Payment + Year + Month */}
                    <FilterBar>
                        <Col xs={24} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Transfer Status
                                </div>
                                <Select
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Statuses</Option>
                                    <Option value="NOT_PAID">Pending</Option>
                                    <Option value="PAID_OUT">Transferred</Option>
                                </Select>
                            </div>
                        </Col>
                        <Col xs={24} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Payment Method
                                </div>
                                <Select
                                    value={paymentMethodFilter}
                                    onChange={setPaymentMethodFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Methods</Option>
                                    <Option value="COD">COD</Option>
                                    <Option value="ONLINE">ONLINE</Option>
                                    <Option value="PREPAID">PREPAID</Option>
                                </Select>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Year
                                </div>
                                <Select
                                    value={yearFilter}
                                    onChange={setYearFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Years</Option>
                                    {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => 2024 + i).reverse().map(y => (
                                        <Option key={y} value={String(y)}>{y}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div>
                                <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-color-title pcu-opacity-50 pcu-letter-spacing-1 pcu-uppercase" style={{ fontFamily: 'var(--font-family-primary)' }}>
                                    Month
                                </div>
                                <Select
                                    value={monthFilter}
                                    onChange={setMonthFilter}
                                    style={{ width: "100%", height: "34px" }}
                                    className="custom-select"
                                >
                                    <Option value="">All Months</Option>
                                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                                        <Option key={i} value={String(i)}>{m}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </FilterBar>
                </div>

                {/* PREMIUM SUMMARY STATS SECTION - EXACTLY MATCHING IMAGE 2 UI STYLE */}
                <Row gutter={[16, 16]} className="pcu-margin-bottom-24" style={{ marginBottom: '24px' }}>
                    {/* Card 1: Total Payouts */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "" ? "active-card" : ""}`}
                            onClick={() => setStatusFilter("")}
                            style={{
                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "" ? '0 8px 24px rgba(37, 99, 235, 0.45)' : '0 4px 14px rgba(37, 99, 235, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <DollarOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <DollarOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                    Total Payouts
                                </div>
                                <div style={{ fontSize: '24px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {stats?.totalRecords || 0}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Card 2: Total Pending */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "NOT_PAID" ? "active-card" : ""}`}
                            onClick={() => setStatusFilter("NOT_PAID")}
                            style={{
                                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "NOT_PAID" ? '0 8px 24px rgba(234, 88, 12, 0.45)' : '0 4px 14px rgba(234, 88, 12, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "NOT_PAID" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "NOT_PAID" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <ClockCircleOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <ClockCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                        Total Pending
                                    </span>
                                    <span style={{
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        padding: '1px 5px',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        fontWeight: 700
                                    }}>
                                        {stats?.pendingPayoutCount || 0} Pending
                                    </span>
                                </div>
                                <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {formatPrice(stats?.totalNotPaidAmount || 0)}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* Card 3: Total Paid */}
                    <Col xs={24} md={8}>
                        <div
                            className={`payout-stat-card ${statusFilter === "PAID_OUT" ? "active-card" : ""}`}
                            onClick={() => setStatusFilter("PAID_OUT")}
                            style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                color: '#fff',
                                position: 'relative',
                                overflow: 'hidden',
                                boxShadow: statusFilter === "PAID_OUT" ? '0 8px 24px rgba(5, 150, 105, 0.45)' : '0 4px 14px rgba(5, 150, 105, 0.25)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '92px',
                                border: statusFilter === "PAID_OUT" ? '2px solid rgba(255, 255, 255, 0.85)' : '2px solid transparent',
                                transform: statusFilter === "PAID_OUT" ? 'scale(1.025)' : 'scale(1)',
                            }}
                        >
                            <div style={{ position: 'absolute', right: -5, bottom: -10, opacity: 0.12 }}>
                                <CheckCircleOutlined style={{ fontSize: '76px', color: '#fff' }} />
                            </div>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                background: 'rgba(255, 255, 255, 0.22)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <CheckCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                            </div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9, lineHeight: 1.2 }}>
                                        Total Paid
                                    </span>
                                    <span style={{
                                        background: 'rgba(255, 255, 255, 0.25)',
                                        padding: '1px 5px',
                                        borderRadius: '4px',
                                        fontSize: '9px',
                                        fontWeight: 700
                                    }}>
                                        {stats?.totalPaidOutCount || 0} Paid
                                    </span>
                                </div>
                                <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', lineHeight: 1.1 }}>
                                    {formatPrice(stats?.totalPaidOutAmount || 0)}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Table View */}
                <div className="pcu-padding-0">
                    <div className="pcu-border-top" style={{ marginTop: '20px' }}>
                        <DataTable
                            columns={columns}
                            data={orders}
                            rowKey="_id"
                            loading={loading}
                            pagination={{
                                current: page,
                                pageSize: pageSize,
                                total: totalOrders,
                                showTotal: (total) => `Total ${total} items`,
                                onChange: (p) => setPage(p),
                                onShowSizeChange: (c, s) => { setPageSize(s); setPage(1); }
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* View Details Modal */}
            <Modal
                open={isModalOpen}
                onCancel={handleCloseModal}
                footer={null}
                width={540}
                closable={true}
                centered
                maskStyle={{ backdropFilter: "blur(4px)" }}
                style={{ borderRadius: 16, overflow: "hidden" }}
                bodyStyle={{ padding: 0 }}
            >
                {selectedRecord && (
                    <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden" }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: "16px 20px 12px",
                            borderBottom: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{
                                    width: 44,
                                    height: 44,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#f8fafc",
                                    padding: 2,
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
                                }}>
                                    <ShopOutlined style={{ fontSize: 20, color: "#4776E6" }} />
                                </div>
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ fontSize: 14, fontWeight: 500, color: "#1e293b" }}>
                                            {selectedRecord.shopId?.name || selectedRecord.shop?.name || "Unknown Shop"}
                                        </span>
                                        <span style={{
                                            padding: '1px 5px',
                                            borderRadius: '3px',
                                            fontSize: '9px',
                                            fontWeight: 600,
                                            backgroundColor: selectedRecord.sellerPayoutStatus === 'PAID_OUT' ? '#e6fbf1' : '#fff9e6',
                                            color: selectedRecord.sellerPayoutStatus === 'PAID_OUT' ? '#10b981' : '#f59e0b',
                                            textTransform: 'uppercase',
                                            border: `1px solid ${selectedRecord.sellerPayoutStatus === 'PAID_OUT' ? '#a7f3d0' : '#fef08a'}`
                                        }}>
                                            {selectedRecord.sellerPayoutStatus === 'PAID_OUT' ? 'Paid Out' : 'Pending'}
                                        </span>
                                        {selectedRecord.payoutReversalHistory?.length > 0 && (
                                            <Tooltip title="This payout was previously reverted">
                                                <span style={{
                                                    padding: '1px 5px',
                                                    borderRadius: '3px',
                                                    fontSize: '9px',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef2f2',
                                                    color: '#ef4444',
                                                    textTransform: 'uppercase',
                                                    border: '1px solid #fca5a5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <ExclamationCircleOutlined /> Reverted Past
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                                        Order: #{selectedRecord.orderId} • Seller: {selectedRecord.shopId?.createdBy ? `${selectedRecord.shopId.createdBy.firstName} ${selectedRecord.shopId.createdBy.lastName}` : "Unknown"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: "16px 20px", maxHeight: "60vh", overflowY: "auto" }}>
                            {/* Transaction Details */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Transaction & Profile Details
                            </div>

                            <div style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '10px 14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                marginBottom: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seller Contact</span>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: '1px' }}>{selectedRecord.shopId?.createdBy?.phone || "No Phone"}</div>
                                        <div style={{ fontSize: 10, color: '#64748b', marginTop: '0px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedRecord.shopId?.createdBy?.email || ""}</div>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer Profile</span>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: '1px' }}>
                                            {selectedRecord.user ? `${selectedRecord.user.firstName} ${selectedRecord.user.lastName}` : "Unknown Customer"}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#64748b', marginTop: '0px' }}>{selectedRecord.user?.phone || ""}</div>
                                    </div>
                                </div>
                                <div style={{ height: '1px', background: '#f1f5f9' }} />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment & Delivery Type</span>
                                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', marginTop: '1px' }}>
                                            {selectedRecord.paymentMethod || selectedRecord.paymentType || selectedRecord.deliveryPaymentMethod || "COD"}
                                            {selectedRecord.paymentCollectionMethod && ` (VIA ${selectedRecord.paymentCollectionMethod.toUpperCase()})`}
                                        </div>
                                        {selectedRecord.transactionId && (
                                            <div style={{ fontSize: 10, color: '#64748b', marginTop: '2px' }}>
                                                TXN: {selectedRecord.transactionId}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivered Date</span>
                                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', marginTop: '1px' }}>
                                            {dayjs(selectedRecord.deliveredAt || selectedRecord.statusHistory?.find(h => h.status === 'delivered')?.timestamp || selectedRecord.updatedAt).format('DD MMM YYYY, hh:mm A')}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ height: '1px', background: '#f1f5f9' }} />
                                <div>
                                    <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payout Date & Time</span>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: '#1e293b', marginTop: '1px' }}>
                                        {selectedRecord.sellerPayoutDate ? dayjs(selectedRecord.sellerPayoutDate).format('DD MMM YYYY, hh:mm A') : "—"}
                                    </div>
                                </div>
                            </div>

                            {/* Location */}
                            {selectedRecord.shopId?.address && (
                                <div style={{
                                    background: "#f0f9ff",
                                    padding: "10px 12px",
                                    borderRadius: 8,
                                    border: "1px solid #e0f2fe",
                                    marginBottom: 12
                                }}>
                                    <div style={{ fontSize: 9, fontWeight: 500, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>
                                        Shop Address Location
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 500, color: "#0369a1", lineHeight: 1.4 }}>
                                        {selectedRecord.shopId.address}
                                    </div>
                                </div>
                            )}

                            {/* Delivery Details */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Delivery Details
                            </div>
                            <div style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '10px 14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                marginBottom: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivery Staff</span>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: '1px' }}>
                                            {selectedRecord.currentAssignedStaff ? `${selectedRecord.currentAssignedStaff.firstName} ${selectedRecord.currentAssignedStaff.lastName}` : "Not Assigned"}
                                        </div>
                                        {selectedRecord.currentAssignedStaff?.phone && (
                                            <div style={{ fontSize: 10, color: '#64748b', marginTop: '0px' }}>{selectedRecord.currentAssignedStaff.phone}</div>
                                        )}
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivery Charges</span>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: '1px' }}>
                                            {formatPrice(selectedRecord.deliveryCharge || 0)}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ height: '1px', background: '#f1f5f9' }} />
                                <div>
                                    <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivered Amount</span>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', marginTop: '1px' }}>
                                        {formatPrice(selectedRecord.totalAmount || 0)}
                                    </div>
                                </div>
                            </div>

                            {/* Products Summary Section */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Product Summary
                            </div>
                            <div style={{ background: "#fff", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 12 }}>
                                {(selectedRecord.items || []).map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: idx !== (selectedRecord.items.length - 1) ? 8 : 0 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b' }}>
                                                {item.productName || item.name || "Product Name"}
                                            </span>
                                            <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                                                Qty: {item.quantity || 1} • Price: {formatPrice(item.price || 0)}
                                            </span>
                                        </div>
                                        <span style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b' }}>
                                            {formatPrice((item.price || 0) * (item.quantity || 1))}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Financial Breakdown */}
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                Financial Ledger Breakdown
                            </div>

                            <div style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                padding: '10px 14px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total MRP Total</span>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#64748b', marginTop: '1px' }}>{formatPrice(getOrderTotals(selectedRecord.items).mrp)}</div>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Base Seller Amount</span>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', marginTop: '1px' }}>{formatPrice(getOrderTotals(selectedRecord.items).sellerPrice)}</div>
                                    </div>
                                </div>
                                <div style={{ height: '1px', background: '#f1f5f9' }} />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coupon Discounts Deductions</span>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#f43f5e', marginTop: '1px' }}>-{formatPrice(selectedRecord.couponDiscount || 0)}</div>
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admin Commissions Profit</span>
                                        <div style={{ fontSize: 13, fontWeight: 500, color: '#4f46e5', marginTop: '1px' }}>
                                            {formatPrice(selectedRecord.totalAmount - (getOrderTotals(selectedRecord.items).sellerPrice - (selectedRecord.couponDiscount || 0)))}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ height: '1px', background: '#f1f5f9' }} />
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                        padding: '6px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid #bbf7d0'
                                    }}>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Net Seller Earnings</span>
                                        <div style={{ fontSize: 14, color: '#15803d', fontWeight: 700, marginTop: '1px' }}>
                                            {formatPrice(getOrderTotals(selectedRecord.items).sellerPrice - (selectedRecord.couponDiscount || 0))}
                                        </div>
                                    </div>
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                                        padding: '6px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd6fe'
                                    }}>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Paid by Customer</span>
                                        <div style={{ fontSize: 14, color: '#5b21b6', fontWeight: 700, marginTop: '1px' }}>
                                            {formatPrice(selectedRecord.totalAmount)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Audit History Section */}
                        {(selectedRecord.payoutReversalHistory && selectedRecord.payoutReversalHistory.length > 0) && (
                            <div style={{ padding: "0 20px 16px" }}>
                                <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                                    Payout Audit History
                                </div>
                                <div style={{
                                    background: '#fff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                }}>
                                    {selectedRecord.payoutReversalHistory.map((history, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            paddingBottom: idx !== selectedRecord.payoutReversalHistory.length - 1 ? '12px' : '0',
                                            borderBottom: idx !== selectedRecord.payoutReversalHistory.length - 1 ? '1px solid #f1f5f9' : 'none'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                <ClockCircleOutlined style={{ fontSize: '12px', color: '#64748b' }} />
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#1e293b' }}>
                                                    {dayjs(history.changedAt).format('DD MMM YYYY, hh:mm A')}
                                                </span>
                                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                                    - Reverted to PENDING by Admin
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: 12,
                                                color: '#ef4444',
                                                background: '#fef2f2',
                                                padding: '6px 10px',
                                                borderRadius: '6px',
                                                border: '1px solid #fee2e2',
                                                marginTop: '4px'
                                            }}>
                                                <strong>Reason:</strong> {history.reason}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div style={{
                            padding: '12px 20px',
                            background: '#f8fafc',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: "1px solid #e2e8f0"
                        }}>
                            <div>
                                {selectedRecord.sellerPayoutStatus === 'PAID_OUT' && (
                                    <Button
                                        danger
                                        icon={<ExclamationCircleOutlined />}
                                        onClick={() => {
                                            setRevertRecord(selectedRecord);
                                            setRevertReason("");
                                            setRevertModalVisible(true);
                                        }}
                                        style={{
                                            borderRadius: 6,
                                            fontWeight: 600,
                                            height: 32,
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        Revert to Pending
                                    </Button>
                                )}
                            </div>
                            <Button
                                onClick={handleCloseModal}
                                style={{
                                    borderRadius: 6,
                                    fontWeight: 500,
                                    height: 32,
                                    padding: '0 16px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '12px',
                                    background: '#fff',
                                    color: '#374151',
                                    boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Reversal Confirmation Modal */}
            <Modal
                open={revertModalVisible}
                onCancel={() => !revertingLoading && setRevertModalVisible(false)}
                footer={null}
                closable={!revertingLoading}
                width={440}
                centered
                maskClosable={false}
                bodyStyle={{ padding: '32px 24px 24px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: '#fef2f2',
                        border: '2px solid #fee2e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                        boxShadow: '0 8px 16px rgba(239, 68, 68, 0.12)'
                    }}>
                        <ExclamationCircleOutlined style={{ fontSize: '32px', color: '#ef4444' }} />
                    </div>

                    <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', margin: '0 0 10px 0', letterSpacing: '-0.5px' }}>
                        Reverse Seller Payout?
                    </h2>

                    <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0', lineHeight: 1.5 }}>
                        You are about to revert a completed payout for Order <strong>#{revertRecord?.orderId}</strong> back to pending. This action should only be used if the payout was marked as paid by mistake.
                        <br /><strong style={{ color: '#ef4444' }}>The seller will be notified about this reversal.</strong>
                    </p>

                    <div style={{ width: '100%', textAlign: 'left', marginBottom: '24px' }}>
                        <div style={{ marginBottom: '8px' }}>
                            <Text strong style={{ fontSize: '13px', color: '#334155' }}>Reason for Reversal <span style={{ color: '#ef4444' }}>*</span></Text>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                            {["Bank transfer failed", "Incorrect payout amount", "Duplicate payout", "Order dispute/refund", "Manual entry mistake"].map((reason) => (
                                <span
                                    key={reason}
                                    onClick={() => setRevertReason(reason)}
                                    style={{
                                        fontSize: '11px',
                                        padding: '4px 10px',
                                        borderRadius: '20px',
                                        background: revertReason === reason ? '#fee2e2' : '#f1f5f9',
                                        color: revertReason === reason ? '#ef4444' : '#475569',
                                        border: revertReason === reason ? '1px solid #fecaca' : '1px solid #e2e8f0',
                                        cursor: 'pointer',
                                        fontWeight: 500,
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    {reason}
                                </span>
                            ))}
                        </div>
                        <Input.TextArea
                            rows={3}
                            placeholder="e.g., Bank transfer failed, Manual mistake..."
                            value={revertReason}
                            onChange={(e) => setRevertReason(e.target.value)}
                            disabled={revertingLoading}
                            style={{ borderRadius: '8px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <Button
                            style={{
                                flex: 1,
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '15px',
                                color: '#475569',
                                border: '1px solid #cbd5e1'
                            }}
                            onClick={() => setRevertModalVisible(false)}
                            disabled={revertingLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            danger
                            style={{
                                flex: 1,
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '14px',
                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                            }}
                            loading={revertingLoading}
                            onClick={confirmReversal}
                        >
                            Inform Seller & Revert
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Paid Confirmation Modal */}
            <Modal
                open={confirmPaidModalVisible}
                onCancel={() => !confirmPaidLoading && setConfirmPaidModalVisible(false)}
                footer={null}
                closable={!confirmPaidLoading}
                width={400}
                centered
                maskClosable={false}
                bodyStyle={{ padding: '32px 24px 24px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: '#f0fdf4',
                        border: '2px solid #dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '20px',
                        boxShadow: '0 8px 16px rgba(22, 163, 74, 0.12)'
                    }}>
                        <CheckCircleOutlined style={{ fontSize: '32px', color: '#16a34a' }} />
                    </div>

                    <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', margin: '0 0 10px 0', letterSpacing: '-0.5px' }}>
                        Confirm Payout
                    </h2>

                    <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 28px 0', lineHeight: 1.5 }}>
                        Are you sure you want to mark the payout for Order <strong>#{confirmPaidRecord?.orderId}</strong> as <strong>PAID</strong>?
                    </p>

                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                        <Button
                            style={{
                                flex: 1,
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '15px',
                                color: '#475569',
                                border: '1px solid #cbd5e1'
                            }}
                            onClick={() => setConfirmPaidModalVisible(false)}
                            disabled={confirmPaidLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            style={{
                                flex: 1,
                                height: '48px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '15px',
                                background: '#16a34a',
                                borderColor: '#16a34a',
                                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
                            }}
                            loading={confirmPaidLoading}
                            onClick={confirmPaidStatus}
                        >
                            Mark as Paid
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SellerPayoutHistory;
